import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Collapse,
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import {
  type PublishedProtocolFood,
  type PublishedProtocolRule,
  type BuilderRuleType,
} from '../../utils/publishedProtocolsStore';
import {
  loadProtocolTemplatesWithDetails,
  type ProtocolTemplateWithDetails,
} from '../../utils/loadDataFromContract';

const RULE_TYPES: BuilderRuleType[] = [
  'MEAL_TIMING',
  'PORTION_LIMIT',
  'PAIRING',
  'AVOIDANCE',
  'CUSTOM',
];

const FOOD_CLASSIFICATIONS: Array<'GREEN' | 'YELLOW' | 'RED'> = ['GREEN', 'YELLOW', 'RED'];

export const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

// ─── Food Form Dialog ────────────────────────────────────────────────────────

export interface FoodDialogProps {
  open: boolean;
  initial?: PublishedProtocolFood;
  onClose: () => void;
  onSave: (food: PublishedProtocolFood) => void;
}

export const FoodDialog = ({ open, initial, onClose, onSave }: FoodDialogProps) => {
  const [name, setName] = useState('');
  const [classification, setClassification] = useState<'GREEN' | 'YELLOW' | 'RED'>('GREEN');
  const [portion, setPortion] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open) {
      setName(initial?.customFoodName ?? '');
      setClassification(initial?.classification ?? 'GREEN');
      setPortion(initial?.portion ?? '');
      setNotes(initial?.notes ?? '');
    }
  }, [open, initial]);

  const handleSave = () => {
    onSave({
      id: initial?.id ?? genId(),
      customFoodName: name.trim(),
      classification,
      portion: portion.trim(),
      notes: notes.trim(),
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{initial ? 'Edit Food Classification' : 'Add Food Classification'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            fullWidth
            label="Food Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            select
            fullWidth
            label="Classification"
            value={classification}
            onChange={(e) => setClassification(e.target.value as 'GREEN' | 'YELLOW' | 'RED')}
          >
            {FOOD_CLASSIFICATIONS.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Portion"
            value={portion}
            onChange={(e) => setPortion(e.target.value)}
          />
          <TextField
            fullWidth
            label="Notes (Optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!name.trim() || !portion.trim()}
        >
          {initial ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Rule Form Dialog ────────────────────────────────────────────────────────

export interface RuleDialogProps {
  open: boolean;
  initial?: PublishedProtocolRule;
  onClose: () => void;
  onSave: (rule: PublishedProtocolRule) => void;
}

export const RuleDialog = ({ open, initial, onClose, onSave }: RuleDialogProps) => {
  const [ruleType, setRuleType] = useState<BuilderRuleType>('MEAL_TIMING');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (open) {
      setRuleType(initial?.ruleType ?? 'MEAL_TIMING');
      setDescription(initial?.description ?? '');
      setIsActive(initial?.isActive ?? true);
    }
  }, [open, initial]);

  const handleSave = () => {
    onSave({
      id: initial?.id ?? genId(),
      ruleType,
      description: description.trim(),
      parameters: initial?.parameters ?? {},
      isActive,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{initial ? 'Edit Rule' : 'Add Rule'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            select
            fullWidth
            label="Rule Type"
            value={ruleType}
            onChange={(e) => setRuleType(e.target.value as BuilderRuleType)}
          >
            {RULE_TYPES.map((rt) => (
              <MenuItem key={rt} value={rt}>
                {rt.replace('_', ' ')}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <TextField
            select
            fullWidth
            label="Active"
            value={isActive ? 'true' : 'false'}
            onChange={(e) => setIsActive(e.target.value === 'true')}
          >
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!description.trim()}>
          {initial ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Template Picker Dialog ──────────────────────────────────────────────────

export interface TemplatePickerDialogProps {
  open: boolean;
  currentTemplateId: string;
  onClose: () => void;
  onSelect: (templateId: string) => void;
}

export const TemplatePickerDialog = ({
  open,
  currentTemplateId,
  onClose,
  onSelect,
}: TemplatePickerDialogProps) => {
  const [templates, setTemplates] = useState<ProtocolTemplateWithDetails[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const templateList = Array.isArray(templates) ? templates : [];

  useEffect(() => {
    if (open) {
      setTemplates(loadProtocolTemplatesWithDetails());
      setExpandedId(currentTemplateId);
    }
  }, [open, currentTemplateId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign Template</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1}>
          {templateList.map((tmpl) => {
            const mealGuidelines = Array.isArray(tmpl.mealGuidelines) ? tmpl.mealGuidelines : [];
            const timingRules = Array.isArray(tmpl.timingRules) ? tmpl.timingRules : [];
            return (
              <Paper
                key={tmpl.id}
                variant="outlined"
                sx={{
                  border: tmpl.id === currentTemplateId ? '2px solid' : undefined,
                  borderColor: tmpl.id === currentTemplateId ? 'primary.main' : undefined,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{ p: 1.5, cursor: 'pointer' }}
                  onClick={() => setExpandedId((prev) => (prev === tmpl.id ? null : tmpl.id))}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{tmpl.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tmpl.targetPopulation}
                    </Typography>
                  </Box>
                  {tmpl.id === currentTemplateId && (
                    <Chip size="small" label="Current" color="primary" sx={{ mr: 1 }} />
                  )}
                  <IconButton size="small">
                    {expandedId === tmpl.id ? (
                      <KeyboardArrowUpIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowDownIcon fontSize="small" />
                    )}
                  </IconButton>
                </Stack>
                <Collapse in={expandedId === tmpl.id} timeout="auto" unmountOnExit>
                  <Box sx={{ px: 2, pb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {tmpl.description}
                    </Typography>
                    <Typography variant="caption" display="block" gutterBottom>
                      <strong>Glucose Targets</strong> — Fasting: {tmpl.glucoseTargets.fasting} ·
                      Post-meal: {tmpl.glucoseTargets.postMeal} · Bedtime:{' '}
                      {tmpl.glucoseTargets.bedtime}
                    </Typography>
                    {mealGuidelines.length > 0 && (
                      <>
                        <Typography
                          variant="caption"
                          display="block"
                          fontWeight={600}
                          sx={{ mt: 1 }}
                        >
                          Meal Guidelines
                        </Typography>
                        {mealGuidelines.map((g, i) => (
                          <Typography key={i} variant="caption" display="block">
                            • {g}
                          </Typography>
                        ))}
                      </>
                    )}
                    {timingRules.length > 0 && (
                      <>
                        <Typography
                          variant="caption"
                          display="block"
                          fontWeight={600}
                          sx={{ mt: 1 }}
                        >
                          Timing Rules
                        </Typography>
                        {timingRules.map((r, i) => (
                          <Typography key={i} variant="caption" display="block">
                            • {r}
                          </Typography>
                        ))}
                      </>
                    )}
                    {tmpl.id !== currentTemplateId && (
                      <Button
                        size="small"
                        variant="contained"
                        sx={{ mt: 1.5 }}
                        onClick={() => {
                          onSelect(tmpl.id);
                          onClose();
                        }}
                      >
                        Assign This Template
                      </Button>
                    )}
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
